import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import axios from 'axios';

import {
  fetchAccountQr,
  type AccountQr,
} from '@/features/accounts/accounts.api';

/** Polling interval for the QR refresh. WhatsApp's Baileys QR rotates roughly
 *  every 20-60 seconds; 15s keeps the dialog showing a fresh image without
 *  spamming the Hub. The dialog passes `enabled=true` while it is open and
 *  `enabled=false` otherwise — closed dialogs do not poll. */
export const QR_REFRESH_INTERVAL_MS = 15_000;

/** React Query key for the per-account QR cache. Deliberately does NOT share a
 *  prefix with `accountsQueryKey = ['accounts']`: react-query prefix-matches
 *  on the query-cache subscribe callback, which combined with
 *  `useAccountUnreadSum`'s cache subscription would re-render every
 *  AccountRow on every QR-cache observer event. */
export const accountQrQueryKey = (accountId: string) =>
  ['account-qr', accountId] as const;

/**
 * React Query hook over `GET /api/v1/accounts/{id}/qr`.
 *
 * Designed for the QR pairing dialog:
 *   - `enabled` is the dialog's `open` prop — closed dialogs do not poll.
 *   - `staleTime: 0` — the QR rotates server-side; every refetch is a fresh fetch.
 *   - `refetchInterval` — periodic poll while the dialog is open.
 *   - `retry: 0` — a 404 / 409 / 503 should bubble immediately so the dialog
 *     can react (close on 404/409, surface a transient on 503).
 *   - On re-open of the dialog, `enabled` flips back to `true` and the cache
 *     is already invalidated by the dialog's `useEffect` cleanup; the next
 *     render fires a fresh fetch.
 */
export function useAccountQr(
  accountId: string,
  options: { enabled: boolean },
): UseQueryResult<AccountQr, Error> {
  return useQuery<AccountQr, Error>({
    queryKey: accountQrQueryKey(accountId),
    queryFn: () => fetchAccountQr(accountId),
    enabled: options.enabled,
    staleTime: 0,
    refetchInterval: options.enabled ? QR_REFRESH_INTERVAL_MS : false,
    retry: 0,
  });
}

/**
 * Pull the HTTP status code off an error thrown by the QR fetch.
 * Returns `null` for non-HTTP errors (e.g. network / parse failures).
 */
export function qrErrorStatus(error: Error | null): number | null {
  if (error === null) return null;
  if (axios.isAxiosError(error) && error.response) {
    return error.response.status;
  }
  return null;
}
